/**
 * @chatsdet/cards-plugins
 *
 * Shared registry for the unified board/card system. A "card" is a generic
 * board item; each board (agent tasks, product items, accounts/CRM) declares
 * which plugin features it opts into via a manifest.
 *
 * The DB shape lives in `packages/db/src/schema.ts` — `cards`, `cardDetails`,
 * `cardComments`, `cardAttachments`, `cardAssignments`.
 */

export type BoardType = 'agent_task' | 'product_item' | 'account';

/**
 * Plugin features a board can opt into. Add a value to `CARD_FEATURES` when
 * introducing a new feature — `CardFeature` is derived from it.
 *
 * Exported as a runtime value, not only a type, so that anything needing to
 * enumerate the features reads this list instead of hand-maintaining a second
 * copy. One did: the registry test kept its own array, `linked_org` was added
 * to the union and to the account board but not there, and the suite went red.
 */
export const CARD_FEATURES = [
  'comments',
  'attachments',
  'assignments',
  'tags',
  'checklists',
  'search',
  'linked_org',
] as const;

export type CardFeature = (typeof CARD_FEATURES)[number];

export type StageOption = {
  value: string;
  label: string;
};

export type BoardManifest = {
  type: BoardType;
  /** Human label used in toggles, headings. */
  label: string;
  /** Phosphor icon name used in the boards toggle. */
  icon: 'ClipboardText' | 'Lightbulb' | 'Buildings';
  /** Path segment under /boards (e.g. 'tasks', 'products', 'accounts'). */
  slug: string;
  /** Stage column for the kanban; the card's `stage` text is interpreted via these. */
  stages: StageOption[];
  /** Plugin features enabled for this board. */
  features: ReadonlyArray<CardFeature>;
};

/**
 * Source of truth for the board catalog. Drives:
 *  - the /boards toggle (BoardsLayout)
 *  - route registration (router.tsx)
 *  - which plugin panels render in CardDrawer
 *  - which plugin endpoints the API exposes per card type
 */
export const BOARDS: Record<BoardType, BoardManifest> = {
  agent_task: {
    type: 'agent_task',
    label: 'Tasks',
    icon: 'ClipboardText',
    slug: 'tasks',
    // Mirrors agentTaskStatusEnum; values must match exactly so kanban
    // grouping by `cards.stage` finds the right column.
    stages: [
      { value: 'waiting', label: 'Waiting' },
      { value: 'actionable', label: 'Actionable' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'completed', label: 'Completed' },
      { value: 'canceled', label: 'Canceled' },
      { value: 'expired', label: 'Expired' },
    ],
    features: ['comments', 'attachments', 'assignments', 'tags', 'search'],
  },
  product_item: {
    type: 'product_item',
    label: 'Product',
    icon: 'Lightbulb',
    slug: 'products',
    // Mirrors the product_item branch of `cards_product_item_stage_check`.
    stages: [
      { value: 'ideas', label: 'Ideas' },
      { value: 'prioritized', label: 'Prioritized' },
      { value: 'in_development', label: 'In Development' },
      { value: 'dogfooding', label: 'Dogfooding' },
      { value: 'beta', label: 'Beta' },
      { value: 'on_hold', label: 'On Hold' },
      { value: 'ga', label: 'GA' },
      { value: 'internal', label: 'Internal' },
      { value: 'deprecated', label: 'Deprecated' },
    ],
    features: ['comments', 'attachments', 'tags', 'search'],
  },
  account: {
    type: 'account',
    label: 'Accounts',
    icon: 'Buildings',
    slug: 'accounts',
    stages: [
      { value: 'prospect', label: 'Prospect' },
      { value: 'qualified', label: 'Qualified' },
      { value: 'engaged', label: 'Engaged' },
      { value: 'trial', label: 'Trial' },
      { value: 'active', label: 'Active' },
      { value: 'churned', label: 'Churned' },
      { value: 'disqualified', label: 'Lost / Disqualified' },
    ],
    features: [
      'comments',
      'attachments',
      'assignments',
      'tags',
      'checklists',
      'search',
      'linked_org',
    ],
  },
};

export const ALL_BOARDS: ReadonlyArray<BoardManifest> = Object.values(BOARDS);

export function getBoardManifest(type: BoardType): BoardManifest {
  return BOARDS[type];
}

export function boardHasFeature(type: BoardType, feature: CardFeature): boolean {
  return BOARDS[type].features.includes(feature);
}

/**
 * Discriminated payload stored in `card_details.data`. Each board narrows here.
 * Add new board variants alongside their manifest entry. Keep keys snake_case
 * to match jsonb conventions elsewhere in the schema.
 */
export type CardDetailsData =
  | ({ board_type: 'agent_task' } & AgentTaskDetails)
  | ({ board_type: 'product_item' } & ProductItemDetails)
  | ({ board_type: 'account' } & AccountDetails);

export type AgentTaskDetails = {
  trigger_type: string;
  trigger_config?: Record<string, unknown>;
  property_github_repo_id?: string | null;
  source_commit?: string | null;
  resolution_commit?: string | null;
  agent_submitted?: boolean;
  priority?: number;
  group_key?: string | null;
  parent_task_id?: string | null;
  expires_at?: string | null;
  triggered_at?: string | null;
  completed_at?: string | null;
};

export type ProductItemDetails = {
  priority?: 'p0' | 'p1' | 'p2' | 'p3' | null;
};

export type AccountDetails = {
  company_name?: string | null;
  domain?: string | null;
  arr?: number | null;
  owner_id?: string | null;
};
